import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';
import { notificationAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Bell,
  BellRinging,
  Check,
  CheckCircle,
  Checks,
  ChatCircle,
  FileText,
  Lightning,
  Star,
  Briefcase,
  CircleNotch,
  FunnelSimple,
  Trash,
} from '@phosphor-icons/react';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [markingAll, setMarkingAll] = useState(false);
  const { notifications: wsNotifications } = useWebSocket();
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationAPI.getAll(filter === 'unread');
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Merge WebSocket notifications in real-time
  useEffect(() => {
    if (wsNotifications.length > 0) {
      setNotifications(prev => {
        const newNotifs = [...wsNotifications];
        const existingIds = new Set(prev.map(n => n.notification_id));
        const uniqueNew = newNotifs.filter(n => !existingIds.has(n.notification_id));
        return [...uniqueNew, ...prev];
      });
    }
  }, [wsNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      window.dispatchEvent(new Event('notifications-updated'));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      window.dispatchEvent(new Event('notifications-updated'));
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await handleMarkRead(notification.notification_id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      proposal: FileText,
      quotation: Briefcase,
      chat: ChatCircle,
      rating: Star,
      project: Lightning,
      commission: CheckCircle,
    };
    return iconMap[type] || Bell;
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      proposal: 'bg-blue-100 text-blue-600',
      quotation: 'bg-orange-100 text-orange-600',
      chat: 'bg-green-100 text-green-600',
      rating: 'bg-amber-100 text-amber-600',
      project: 'bg-purple-100 text-purple-600',
      commission: 'bg-emerald-100 text-emerald-600',
    };
    return colorMap[type] || 'bg-zinc-100 text-zinc-600';
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'Hace un momento';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} d`;
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-orange-500 animate-spin" data-testid="notifications-loading" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Notificaciones</h1>
          <p className="text-zinc-600 dark:text-zinc-400">
            {unreadCount > 0
              ? `Tienes ${unreadCount} notificación${unreadCount > 1 ? 'es' : ''} sin leer`
              : 'Todas las notificaciones leídas'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-none border-zinc-300"
              onClick={handleMarkAllRead}
              disabled={markingAll}
              data-testid="mark-all-read-btn"
            >
              {markingAll ? (
                <CircleNotch className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Checks className="w-4 h-4 mr-1" />
              )}
              Marcar todas como leídas
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2" data-testid="notification-filters">
        <FunnelSimple className="w-4 h-4 text-zinc-500" />
        <Button
          variant={filter === 'all' ? 'default' : 'ghost'}
          size="sm"
          className={`rounded-none ${filter === 'all' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
          onClick={() => setFilter('all')}
          data-testid="filter-all-btn"
        >
          Todas
          <Badge variant="secondary" className="ml-1.5 rounded-none text-xs bg-zinc-200 text-zinc-700">
            {notifications.length}
          </Badge>
        </Button>
        <Button
          variant={filter === 'unread' ? 'default' : 'ghost'}
          size="sm"
          className={`rounded-none ${filter === 'unread' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
          onClick={() => setFilter('unread')}
          data-testid="filter-unread-btn"
        >
          Sin leer
          {unreadCount > 0 && (
            <Badge className="ml-1.5 rounded-none text-xs bg-red-500 text-white">
              {unreadCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Notification List */}
      {notifications.length === 0 ? (
        <Card className="border-zinc-200 rounded-none">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">
              {filter === 'unread' ? 'Sin notificaciones nuevas' : 'No hay notificaciones'}
            </h3>
            <p className="text-zinc-500 max-w-sm mx-auto">
              {filter === 'unread'
                ? 'Ya leíste todas tus notificaciones. ¡Buen trabajo!'
                : 'Aquí aparecerán las notificaciones cuando recibas propuestas, mensajes o actualizaciones.'}
            </p>
            {filter === 'unread' && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-4 text-orange-600"
                onClick={() => setFilter('all')}
                data-testid="show-all-btn"
              >
                Ver todas las notificaciones
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {notifications.map((notification) => {
            const Icon = getNotificationIcon(notification.type);
            const colorClass = getNotificationColor(notification.type);

            return (
              <Card
                key={notification.notification_id}
                className={`border-zinc-200 rounded-none cursor-pointer transition-all hover:shadow-[2px_2px_0px_0px_rgba(9,9,11,0.1)] ${
                  !notification.read ? 'border-l-4 border-l-orange-500 bg-orange-50/50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
                data-testid={`notification-item-${notification.notification_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-5 h-5" weight={notification.read ? 'regular' : 'bold'} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`text-sm ${!notification.read ? 'font-semibold text-zinc-900' : 'font-medium text-zinc-700'}`}>
                          {notification.title}
                        </h3>
                        <span className="text-xs text-zinc-400 flex-shrink-0 whitespace-nowrap">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="rounded-none text-xs capitalize">
                          {notification.type}
                        </Badge>
                        {notification.link && (
                          <span className="text-xs text-orange-600 hover:underline">
                            Ver detalle
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Read indicator */}
                    {!notification.read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkRead(notification.notification_id);
                        }}
                        className="flex-shrink-0 p-1 hover:bg-zinc-100 transition-colors"
                        title="Marcar como leída"
                        data-testid={`mark-read-${notification.notification_id}`}
                      >
                        <Check className="w-4 h-4 text-zinc-400 hover:text-orange-500" />
                      </button>
                    )}
                    {notification.read && (
                      <div className="flex-shrink-0 p-1">
                        <CheckCircle className="w-4 h-4 text-green-500" weight="fill" />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
